import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntil, timeout } from 'rxjs/operators';
import { Subject, EMPTY, Subscription } from 'rxjs';
import { take, filter, switchMap } from 'rxjs/operators';

import {
  OrderPanelComponent,
  OrderPanel,
} from '../../shared/components/order-panel/order-panel.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { OrderService } from '../../core/services/order.service';
import { AuthService } from '../../core/services/auth.service';
import { PaginatedOrderResponse } from '../../core/models/order.model';
import {
  buildRetryCartQueryParams,
  RetryCartLineRef,
} from '../../core/utils/order-retry-cart.util';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { UserFacade } from '../../core/store/user/user.facade';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';

@Component({
  selector: 'umkt-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OrderPanelComponent,
    BreadcrumbComponent,
    LoadingComponent,
    CustomInputComponent,
    ErrorDisplayComponent,
    I18nPipe,
  ],
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
})
export class OrdersComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Minha conta', url: '/my-account' },
    { label: 'Pedidos', url: '/my-account/orders' },
  ];

  currentPage: number = 0;
  itemsPerPage: number = 5;
  private baseItemsPerPageOptions: number[] = [5, 10, 15, 20];
  currentOrders: OrderPanel[] = [];
  allOrders: OrderPanel[] = [];
  isLoading: boolean = false;
  error: string | null = null;
  preservePanelState: boolean = false;
  selectedFilter = 'all';
  selectedOrder = 'date-newest';
  searchTerm = '';

  totalElements: number = 0;
  totalPagesFromServer: number = 0;

  currentUserId: string | null = null;
  isSelectorFocused: boolean = false;

  private destroy$ = new Subject<void>();

  private _itemsPerPageOptions: number[] = [];
  private _itemsPerPageSelectOptions: { value: string; label: string }[] = [];
  private _lastTotalElements: number = -1;

  retryCount = 0;

  private langSubscription?: Subscription;

  filterOptions = [
    { value: 'all', label: 'pages.orders.filters.all' },
    { value: 'status-em_analise', label: 'pages.orders.filters.analysis' },
    { value: 'status-cancelado', label: 'pages.orders.filters.cancelled' },
    { value: 'status-completo', label: 'pages.orders.filters.completed' },
  ];

  orderOptions = [
    { value: 'date-newest', label: 'pages.orders.sort.newest' },
    { value: 'date-oldest', label: 'pages.orders.sort.oldest' },
    { value: 'price-desc', label: 'pages.orders.sort.priceDesc' },
    { value: 'price-asc', label: 'pages.orders.sort.priceAsc' },
  ];
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private authService = inject(AuthService);
  private userFacade = inject(UserFacade);
  private i18nService = inject(I18nService);


  ngOnInit() {
    this.currentUserId = this.authService.getCurrentUserUuid();

    this.langSubscription = this.i18nService
      .getCurrentLanguage()
      .subscribe(() => {
        this.updateBreadcrumb();
      });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['page'] !== undefined) {
        this.currentPage = +params['page'] - 1;
      }
      if (params['size'] !== undefined) {
        this.itemsPerPage = +params['size'];
      }
      if (params['status'] !== undefined) {
        this.selectedFilter = `status-${params['status']}`;
      }
      if (params['sort'] !== undefined) {
        this.selectedOrder = params['sort'];
      }
      if (params['search'] !== undefined) {
        this.searchTerm = params['search'];
      }

      this.updateBreadcrumb();

      if (this.currentUserId) {
        this.loadCustomerOrders();
      }
    });
  }

  private getOrderParams(orderValue: string) {
    switch (orderValue) {
      case 'date-newest':
        return {
          sortField: 'createdAt',
          sortDirection: 'desc',
        };
      case 'date-oldest':
        return {
          sortField: 'createdAt',
          sortDirection: 'asc',
        };
      case 'price-desc':
        return {
          sortField: 'total',
          sortDirection: 'desc',
        };
      case 'price-asc':
        return {
          sortField: 'total',
          sortDirection: 'asc',
        };
      default:
        return {
          sortField: 'createdAt',
          sortDirection: 'desc',
        };
    }
  }

  private getStatusFilter(filterValue: string): string | null {
    switch (filterValue) {
      case 'status-em_analise':
        return 'em_analise';
      case 'status-cancelado':
        return 'cancelado';
      case 'status-completo':
        return 'completo';
      default:
        return null;
    }
  }

  private getStatusFilterForApi(filterValue: string): string | null {
    switch (filterValue) {
      case 'status-em_analise':
        return 'analysis';
      case 'status-cancelado':
        return 'cancelled';
      case 'status-completo':
        return 'completed';
      default:
        return null;
    }
  }

  private getBackendStatusesForApi(filterValue: string): string[] | null {
    switch (filterValue) {
      case 'status-completo':
        return [
          'completed',
          'partial_completed',
          'created',
          'success',
          'done',
          'sucesso',
          'approved',
          'paid',
          'delivered',
        ];
      case 'status-em_analise':
        return [
          'in_progress',
          'analysis',
          'pending_activation',
          'on_hold',
          'pending',
          'processing',
          'analyzing',
          'awaiting-cpc',
        ];
      case 'status-cancelado':
        return [
          'failed_cle',
          'failed_credit',
          'cancelled',
          'refunded',
          'failed',
          'not_found',
          'bad_request',
          'rejected',
          'canceled',
        ];
      default:
        return null;
    }
  }

  handleRetry() {
    this.retryCount++;
    this.loadCustomerOrders();
  }

  loadCustomerOrders() {
    console.log('loadCustomerOrders');
    const userUuid =
      this.currentUserId || this.authService.getCurrentUserUuid();

    if (!userUuid) {
      this.error = this.i18nService.instant('pages.orders.errors.auth');
      this.router.navigate(['/login']);
      return;
    }

    // Aguarda os dados do usuário serem carregados (importante após refresh)
    this.userFacade
      .getUserDetails()
      .pipe(
        filter(user => user !== null), // Aguarda até que os dados estejam disponíveis
        timeout(5000), // Timeout de 5 segundos para evitar espera indefinida
        take(1),
        switchMap(user => {
          if (!user || !user.document) {
            this.error = this.i18nService.instant('pages.orders.errors.cnpj');
            this.isLoading = false;
            return EMPTY;
          }

          this.isLoading = true;
          this.error = null;

          const orderParams = this.getOrderParams(this.selectedOrder);
          const apiStatuses = this.getBackendStatusesForApi(
            this.selectedFilter
          );

          const baseParams = {
            page: this.currentPage,
            size: this.itemsPerPage,
            sortField: orderParams.sortField || 'createdAt',
            sortDirection: orderParams.sortDirection || 'desc',
            status: apiStatuses || undefined,
            searchTerm: this.searchTerm || undefined,
          };

          const request$ = this.authService.isFixaSession()
            ? this.userFacade.getSelectedContractDetails().pipe(
                take(1),
                switchMap(details =>
                  this.orderService.getCustomerOrdersPaginated(
                    userUuid,
                    user.document,
                    {
                      ...baseParams,
                      contractId: details?.customerAccountId,
                    }
                  )
                )
              )
            : this.orderService.getCustomerOrdersPaginated(
                userUuid,
                user.document,
                baseParams
              );

          return request$;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: PaginatedOrderResponse) => {
          if (response && response.content) {
            const mappedOrders = response.content
              .filter(order => OrderPanelComponent.shouldDisplayOrder(order))
              .map(order =>
                OrderPanelComponent.mapCustomerOrderToOrderPanel(order)
              );

            this.currentOrders = mappedOrders;
            this.allOrders = mappedOrders;

            this.totalElements = response.totalElements;
            this.totalPagesFromServer = response.totalPages;
            this.clearCache();
          } else {
            this.allOrders = [];
            this.currentOrders = [];
            this.totalElements = 0;
            this.totalPagesFromServer = 0;
            this.clearCache();
          }
          this.retryCount = 0;
          this.isLoading = false;
        },
        error: error => {
          console.error('Erro ao carregar pedidos:', error);
          if (error.name === 'TimeoutError') {
            this.error =
              'Tempo limite excedido ao carregar dados do usuário. Por favor, tente novamente.';
          } else {
            this.error = this.i18nService.instant('pages.orders.errors.load');
          }
          this.isLoading = false;
          this.allOrders = [];
          this.currentOrders = [];
          this.totalElements = 0;
          this.totalPagesFromServer = 0;
          this.clearCache();
        },
      });
  }


  private clearCache() {
    this._itemsPerPageOptions = [];
    this._itemsPerPageSelectOptions = [];
    this._lastTotalElements = -1;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.updateUrlParams();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onItemsPerPageChange(newItemsPerPage: number) {
    const itemsPerPage = Number(newItemsPerPage);
    if (isNaN(itemsPerPage) || itemsPerPage <= 0) {
      console.error('Invalid itemsPerPage value:', newItemsPerPage);
      return;
    }

    this.itemsPerPage = itemsPerPage;
    this.currentPage = 0;
    this.updateUrlParams();
  }

  private updateUrlParams(): void {
    const queryParams: any = {};

    if (this.currentPage > 0) {
      queryParams.page = this.currentPage + 1;
    }
    if (this.itemsPerPage !== 5) {
      queryParams.size = this.itemsPerPage;
    }
    if (this.selectedFilter && this.selectedFilter !== 'all') {
      queryParams.status = this.selectedFilter.replace('status-', '');
    }
    if (this.selectedOrder && this.selectedOrder !== 'date-newest') {
      queryParams.sort = this.selectedOrder;
    }
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      queryParams.search = this.searchTerm.trim();
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });

    // O queryParams.subscribe() já vai chamar o load quando a URL mudar
  }

  private updateBreadcrumb(): void {
    const queryParams = this.route.snapshot.queryParams;
    const paginationParams: any = {};

    if (queryParams['page']) {
      paginationParams.page = queryParams['page'];
    }
    if (queryParams['size']) {
      paginationParams.size = queryParams['size'];
    }
    if (queryParams['status']) {
      paginationParams.status = queryParams['status'];
    }
    if (queryParams['sort']) {
      paginationParams.sort = queryParams['sort'];
    }
    if (queryParams['search']) {
      paginationParams.search = queryParams['search'];
    }

    if (!paginationParams.page && this.currentPage > 0) {
      paginationParams.page = this.currentPage + 1;
    }
    if (!paginationParams.size && this.itemsPerPage !== 5) {
      paginationParams.size = this.itemsPerPage;
    }
    if (
      !paginationParams.status &&
      this.selectedFilter &&
      this.selectedFilter !== 'all'
    ) {
      paginationParams.status = this.selectedFilter.replace('status-', '');
    }
    if (
      !paginationParams.sort &&
      this.selectedOrder &&
      this.selectedOrder !== 'date-newest'
    ) {
      paginationParams.sort = this.selectedOrder;
    }
    if (
      !paginationParams.search &&
      this.searchTerm &&
      this.searchTerm.trim() !== ''
    ) {
      paginationParams.search = this.searchTerm.trim();
    }

    this.breadcrumbItems = [
      {
        label: this.i18nService.instant('shared.labels.myAccount'),
        url: '/my-account',
      },
      {
        label: this.i18nService.instant('shared.labels.orders'),
        url: '/my-account/orders',
        queryParams:
          Object.keys(paginationParams).length > 0
            ? paginationParams
            : undefined,
      },
    ];
  }

  onItemsPerPageSelectorChange(newItemsPerPage: number) {
    this.onItemsPerPageChange(newItemsPerPage);
  }

  onSelectorFocus() {
    this.isSelectorFocused = true;
  }

  onSelectorBlur() {
    this.isSelectorFocused = false;
  }

  onViewPlans() {
    this.router.navigate(['/my-account/plans']);
  }

  onOrderDetails(payload: { uuid: string; creditAnalysisRejected: boolean }) {
    const orderId = payload?.uuid;
    if (!orderId) {
      return;
    }
    this.orderService.setSelectedOrderId(orderId);
    this.orderService.setOrderDetailsCreditContext(
      orderId,
      payload.creditAnalysisRejected === true
    );

    const paginationParams: any = {};

    if (this.currentPage > 0) {
      paginationParams.page = this.currentPage + 1;
    }
    if (this.itemsPerPage !== 5) {
      paginationParams.size = this.itemsPerPage;
    }
    if (this.selectedFilter && this.selectedFilter !== 'all') {
      paginationParams.status = this.selectedFilter.replace('status-', '');
    }
    if (this.selectedOrder && this.selectedOrder !== 'date-newest') {
      paginationParams.sort = this.selectedOrder;
    }
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      paginationParams.search = this.searchTerm.trim();
    }

    const queryParams =
      payload.creditAnalysisRejected === true ? { creditRejected: '1' } : {};

    this.router.navigate(['/my-account/orders/details', orderId], {
      queryParams,
      state: {
        previousQueryParams:
          Object.keys(paginationParams).length > 0
            ? paginationParams
            : undefined,
        creditAnalysisRejected: payload.creditAnalysisRejected === true,
      },
    });
  }

  onRetryWithCard(order: OrderPanel): void {
    const lines = this.retryLinesFromPanel(order);
    const params = buildRetryCartQueryParams(lines);
    if (!params) {
      return;
    }
    this.router.navigate(['/'], { queryParams: params });
  }

  private retryLinesFromPanel(order: OrderPanel): RetryCartLineRef[] {
    const products = Array.isArray(order.products) ? order.products : [];
    return products
      .filter(p => !!p?.variationUuid)
      .map(p => ({
        quantity: p.quantity ?? 1,
        productUuid: p.variationUuid!,
        parentProductUuid: p.parentProductUuid,
      }));
  }

  get totalPages(): number {
    return this.totalPagesFromServer;
  }

  get totalItems(): number {
    return this.totalElements;
  }

  get itemsPerPageOptions(): number[] {
    if (
      this._lastTotalElements === this.totalElements &&
      this._itemsPerPageOptions.length > 0
    ) {
      return this._itemsPerPageOptions;
    }

    this._lastTotalElements = this.totalElements;
    const totalItems = this.totalItems;

    if (totalItems === 0) {
      this._itemsPerPageOptions = [5, 10, 15, 20];
      return this._itemsPerPageOptions;
    }

    const validOptions = this.baseItemsPerPageOptions.filter(
      option => option <= totalItems
    );

    const nextLargerOption = this.baseItemsPerPageOptions.find(
      option => option > totalItems
    );

    if (nextLargerOption && !validOptions.includes(nextLargerOption)) {
      validOptions.push(nextLargerOption);
    }

    if (validOptions.length === 0) {
      this._itemsPerPageOptions = [5, 10, 15, 20];
    } else {
      this._itemsPerPageOptions = validOptions.sort((a, b) => a - b);
    }

    if (!this._itemsPerPageOptions.includes(this.itemsPerPage)) {
      this.itemsPerPage = Math.max(...this._itemsPerPageOptions);
    }

    return this._itemsPerPageOptions;
  }

  get itemsPerPageSelectOptions(): { value: string; label: string }[] {
    const currentOptions = this.itemsPerPageOptions;
    const optionsHash = currentOptions.join(',');
    const currentHash = this._itemsPerPageSelectOptions
      .map(opt => opt.value)
      .join(',');

    if (optionsHash === currentHash) {
      return this._itemsPerPageSelectOptions;
    }

    this._itemsPerPageSelectOptions = currentOptions.map(option => ({
      value: option.toString(),
      label: option.toString(),
    }));

    return this._itemsPerPageSelectOptions;
  }

  onItemsPerPageSelectChange(value: string) {
    const newItemsPerPage = Number(value);
    this.onItemsPerPageChange(newItemsPerPage);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  onOrderChange(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  getPageInfoText(): string {
    if (this.totalElements === 0) {
      return 'pages.orders.paginationInfoEmpty';
    }
    return 'pages.orders.paginationInfo';
  }

  getVisiblePages(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: number[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 0; i < 5; i++) {
          visiblePages.push(i);
        }
        visiblePages.push(-1);
        visiblePages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        visiblePages.push(0);
        visiblePages.push(-1);
        for (let i = totalPages - 5; i < totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        visiblePages.push(0);
        visiblePages.push(-1);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          visiblePages.push(i);
        }
        visiblePages.push(-1);
        visiblePages.push(totalPages - 1);
      }
    }

    return visiblePages;
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages && page !== this.currentPage) {
      this.onPageChange(page);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.goToPage(this.currentPage + 1);
    }
  }

  onSearch(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  getPageInfoParams(): any {
    if (this.totalElements === 0) return {};

    const startItem = this.currentPage * this.itemsPerPage + 1;
    const endItem = Math.min(
      (this.currentPage + 1) * this.itemsPerPage,
      this.totalElements
    );

    return {
      start: startItem,
      end: endItem,
      total: this.totalElements,
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatItemsPerPageOption(option: number): string {
    return option.toString().padStart(2, '0');
  }
}
